import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, Info } from "lucide-react";
import { PageHeader } from "./fordon";
import { RAPIDO_MODELS } from "@/data/rapido-models";

const URL_SELF = "https://www.akhusbilfritid.se/rapido/prislista";

const FAQ = [
  {
    q: "Är Rapidos prislista publik?",
    a: "Ja, Rapido publicerar årliga prislistor per marknad. Priserna nedan är våra ungefärliga svenska riktpriser 2024–2025 — det exakta priset beror på chassival, motoreffekt, färg, växellåda (manuell/automat), utrustningspaket och eventuella individuella tillval. Vi räknar fram ett konkret pris för dig när du hört av dig.",
  },
  {
    q: "Ingår leverans till Kalix i priserna?",
    a: "Priserna gäller hämtning i Kalix. Vi kan ordna transport i hela Sverige — kostnaden beräknas per uppdrag baserat på destination.",
  },
  {
    q: "Vad skiljer priserna mellan modellerna?",
    a: "Storlek, byggkonstruktion och isolering. Plåthusbilar (V-serien) är billigast eftersom de använder skåpbilens originalkaross. Halvintegrerade får ett eget karossaggregat och dubbla golv. Integrerade får också en ny frontkaross i komposit — det är det dyraste men också det mest välisolerade.",
  },
  {
    q: "Hur ofta ändras priserna?",
    a: "Rapido justerar oftast en gång per år, i samband med lansering av ny årsmodell (augusti–september). Chassipriser från Fiat/Citroën kan påverkas oftare — vi meddelar alltid gällande pris när du beställer.",
  },
  {
    q: "Kan jag byta in min gamla husbil?",
    a: "Ja. Vi tar in inbyten — värdet räknas av på köpeskillingen. Ring 070-862 97 43 så bokar vi värdering.",
  },
];

export const Route = createFileRoute("/rapido/prislista")({
  head: () => ({
    meta: [
      { title: "Rapido husbilar prislista 2024–2025 | AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Rapido husbilar prislista 2024–2025: V55, V62, V68, 866F, 9005 DFH. Nypris och begagnat prisintervall. Officiell återförsäljare i Kalix.",
      },
      { name: "keywords", content: "rapido husbilar prislista, rapido pris, rapido nypris, rapido begagnad pris, rapido v62 pris, rapido v68 pris, rapido 866f pris" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Rapido husbilar prislista 2024–2025" },
      { property: "og:description", content: "Ungefärliga svenska priser för hela Rapido-modellprogrammet, ny och begagnad." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Rapido", item: "https://www.akhusbilfritid.se/rapido" },
            { "@type": "ListItem", position: 3, name: "Prislista", item: URL_SELF },
          ],
        }),
      },
    ],
  }),
  component: Prislista,
});

function Prislista() {
  return (
    <>
      <PageHeader
        label="Rapido · Prislista · 2024–2025"
        title={<>Rapido husbilar <span className="font-italic-display text-accent">prislista</span>.</>}
        sub="Riktpriser i svenska kronor för Rapidos aktuella modellprogram. Exakt pris beror på chassi, utrustning och individuella tillval — vi räknar fram en konkret offert när du hör av dig."
      />

      <section className="container-edge py-12">
        <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
          <Info className="mt-0.5 shrink-0 text-accent" size={18} />
          <p>
            Priserna nedan är våra ungefärliga svenska riktpriser för årsmodell 2024–2025 baserat på gällande Rapido-prislista och normal utrustningsnivå. Priserna kan ändras utan förvarning. För bindande pris — kontakta oss.
          </p>
        </div>
      </section>

      <section className="container-edge pb-12">
        <div className="overflow-hidden rounded-3xl border border-border bg-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left">
                <tr>
                  <th className="px-6 py-4 font-display text-base">Modell</th>
                  <th className="px-6 py-4 font-display text-base">Kategori</th>
                  <th className="px-6 py-4 font-display text-base">Längd</th>
                  <th className="px-6 py-4 font-display text-base">Nypris (från)</th>
                  <th className="px-6 py-4 font-display text-base">Begagnat</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {RAPIDO_MODELS.filter((m) => m.slug !== "platis").map((m) => (
                  <tr key={m.slug} className="border-t border-border align-top">
                    <td className="px-6 py-5 font-medium">{m.name}</td>
                    <td className="px-6 py-5 text-muted-foreground">{m.category}</td>
                    <td className="px-6 py-5 text-muted-foreground">{m.lengthCm} cm</td>
                    <td className="px-6 py-5">{m.priceNew.replace("Från ca ", "").replace(" (2024–2025)", "")}</td>
                    <td className="px-6 py-5 text-muted-foreground">{m.priceUsed.replace("Ca ", "").replace(" beroende på år", "").replace(" beroende på år och utrustning", "").replace(" beroende på modell och år", "")}</td>
                    <td className="px-6 py-5">
                      <Link
                        to="/rapido/modeller/$model"
                        params={{ model: m.slug }}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-accent"
                      >
                        Detaljer <ArrowRight size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="container-edge py-16 md:py-20">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <p className="eyebrow">Vad påverkar slutpriset?</p>
            <h2 className="mt-3 font-display text-4xl">Från listpris till <span className="font-italic-display text-accent">ditt pris</span>.</h2>
            <ul className="mt-8 space-y-4 text-foreground/85">
              {[
                { t: "Motoreffekt", d: "140 hk är standard, 160/180 hk är populära uppgraderingar — särskilt för tyngre halvintegrerade och integrerade." },
                { t: "Växellåda", d: "Fiat AT9 (9-stegs automat) kostar 40 000–60 000 kr extra men är ovärderlig för dig som kör mycket i berg." },
                { t: "Värmesystem", d: "Truma Combi 6 D (diesel) istället för gas: ca 30 000–50 000 kr. Alde vattenburen i integrerade: från fabrik." },
                { t: "Batteri", d: "Litium 100–200 Ah istället för standard AGM: ca 25 000–70 000 kr — ger dubbla eller tredubbla frikampingsdygn." },
                { t: "Solpaneler", d: "150–300 W monterat: 10 000–20 000 kr. Nödvändigt för långa vistelser utan el." },
                { t: "Utrustningspaket", d: "Rapido har flera paket (Style, Design, Nordic) som samlar populära tillval till ett paketpris." },
              ].map((row) => (
                <li key={row.t}>
                  <p className="font-medium">{row.t}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{row.d}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <h3 className="font-display text-3xl">Räkna ut ditt pris</h3>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Vi hjälper dig kostnadsfritt att räkna igenom vad ditt drömpaket faktiskt kostar — inklusive inbyte, finansiering och månadskostnad. Inga överraskningar.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
                <Phone size={16} /> 070-862 97 43
              </a>
              <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold transition hover:bg-secondary">
                Skicka förfrågan
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-warm">
        <div className="container-edge py-16 md:py-24">
          <p className="eyebrow">Vanliga frågor om prislistan</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl">Om <span className="font-italic-display text-accent">priserna</span>.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQ.map((f) => (
              <div key={f.q} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-xl">{f.q}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
